import React from 'react';
import { FastField, Field, type FieldProps } from 'formik';
import { dayjs, parseDateOnly, toDateString } from '@koob/dates';
import {
  KoobInputDate,
  KoobInputDateRange,
  type KoobDateRange,
  type KoobDateRangePreset,
} from '../KoobInputDate';
const FieldLabel = ({
  label,
  required,
}: {
  label: React.ReactNode;
  required?: boolean;
}) => (
  <label className="m-0 flex items-center gap-1 text-sm font-medium text-gray-700">
    {label}
    {required && <span className="text-red-500">*</span>}
  </label>
);

const FieldError = ({ error }: { error: React.ReactNode }) => (
  <p className="m-0 text-sm text-red-500">{error}</p>
);

type CommonFieldProps = {
  name: string;
  label?: React.ReactNode;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
  minDate?: Date;
  maxDate?: Date;
  /** Show the error before the field is touched when false */
  touchRequired?: boolean;
  /** Use formik FastField (skips re-renders of untouched fields) */
  fast?: boolean;
  /** @deprecated use clearable */
  isClearable?: boolean;
  clearable?: boolean;
};

type KoobInputDateFieldProps = CommonFieldProps & {
  /** Rendered as data-testid on the wrapper. */
  testId?: string;
  /** Orange frame for a value that is valid but worth a second look. */
  warning?: boolean;
  /** @deprecated use label */
  datePlaceholder?: React.ReactNode;
  /** @deprecated use label (label only renders when this is not false) */
  showPlaceHolder?: boolean;
};

export const KoobInputDateField = ({
  name,
  label,
  datePlaceholder,
  showPlaceHolder = true,
  required,
  placeholder,
  disabled,
  minDate,
  maxDate,
  touchRequired = true,
  fast = false,
  clearable,
  isClearable,
  warning = false,
  testId,
}: KoobInputDateFieldProps) => {
  const FieldComponent = fast ? FastField : Field;
  const labelContent = label ?? (showPlaceHolder ? datePlaceholder : null);

  return (
    <FieldComponent name={name}>
      {({ field, meta, form }: FieldProps) => {
        const hasError = touchRequired
          ? Boolean(meta.error && meta.touched)
          : Boolean(meta.error);

        return (
          <div
            className="relative flex w-full flex-1 flex-col gap-2"
            data-testid={testId}
          >
            {Boolean(labelContent) && (
              <FieldLabel label={labelContent} required={required} />
            )}
            <KoobInputDate
              block
              hasError={hasError}
              hasWarning={warning}
              placeholder={placeholder}
              disabled={disabled}
              minDate={minDate}
              maxDate={maxDate}
              clearable={Boolean(clearable ?? isClearable)}
              value={parseDateOnly(field.value)}
              onChange={(value) => form.setFieldValue(name, value)}
            />
            {hasError && <FieldError error={meta.error} />}
          </div>
        );
      }}
    </FieldComponent>
  );
};

type KoobInputDateRangeFieldProps = CommonFieldProps & {
  /** Store Dates/dayjs objects (keeping time-of-day when the value is a dayjs object) instead of 'YYYY-MM-DD' strings */
  isDateTime?: boolean;
  /** Keep the stored value's time-of-day, only move the calendar date */
  updateOnlyDate?: boolean;
  /** Quick-select sidebar entries; hidden by default in forms */
  presets?: KoobDateRangePreset[];
  /** Rendered above the calendar */
  header?: React.ReactNode;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  onComplete?: (range: KoobDateRange) => void;
};

export const KoobInputDateRangeField = ({
  name,
  label,
  required,
  placeholder,
  disabled,
  minDate,
  maxDate,
  touchRequired = true,
  fast = false,
  clearable,
  isClearable = true,
  isDateTime = false,
  updateOnlyDate = false,
  presets = [],
  header,
  defaultOpen,
  onOpenChange,
  onComplete,
}: KoobInputDateRangeFieldProps) => {
  const FieldComponent = fast ? FastField : Field;

  return (
    <FieldComponent name={name}>
      {({ field, meta, form }: FieldProps) => {
        const hasError = touchRequired
          ? Boolean(meta.error && meta.touched)
          : Boolean(meta.error);

        const handleChange = (range: KoobDateRange | null) => {
          if (!range?.from || !range?.to) {
            if (!range) form.setFieldValue(name, [null, null]);
            return;
          }

          if (
            updateOnlyDate ||
            (isDateTime && dayjs.isDayjs(field.value?.[0]))
          ) {
            // Keep the original time-of-day, only move the calendar date.
            // Stays a dayjs so a zoned value (hotel tz) keeps its zone.
            const start = dayjs(field.value?.[0])
              .year(range.from.getFullYear())
              .month(range.from.getMonth())
              .date(range.from.getDate());
            const end = dayjs(field.value?.[1])
              .year(range.to.getFullYear())
              .month(range.to.getMonth())
              .date(range.to.getDate());
            form.setFieldValue(name, [start, end]);
            return;
          }

          form.setFieldValue(
            name,
            isDateTime
              ? [range.from, range.to]
              : [toDateString(range.from), toDateString(range.to)],
          );
        };

        return (
          <div className="relative flex w-full flex-1 flex-col gap-2">
            {Boolean(label) && <FieldLabel label={label} required={required} />}
            <KoobInputDateRange
              block
              presets={presets}
              header={header}
              hasError={hasError}
              placeholder={placeholder}
              disabled={disabled}
              minDate={minDate}
              maxDate={maxDate}
              clearable={clearable ?? isClearable}
              defaultOpen={defaultOpen}
              onOpenChange={onOpenChange}
              onComplete={onComplete}
              value={{
                from: parseDateOnly(field.value?.[0]) ?? undefined,
                to: parseDateOnly(field.value?.[1]) ?? undefined,
              }}
              onChange={handleChange}
            />
            {hasError && <FieldError error={meta.error} />}
          </div>
        );
      }}
    </FieldComponent>
  );
};
